import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Briefcase, ChevronRight, ListTree, Eye, EyeOff, RotateCcw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { confirmDialog } from '../components/ConfirmHost';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JobTypesPage = () => {
  const { canCreate, canEdit, canDelete } = useAuth();
  const [jobTypes, setJobTypes] = useState([]);
  const [jobKindsCounts, setJobKindsCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showPassive, setShowPassive] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Aktif iş çeşidi sayıları sunucuda hesaplanır
      const typesRes = await axios.get(`${API}/job-types/all`, { params: { with_counts: true } });
      setJobTypes(typesRes.data);
      setJobKindsCounts(Object.fromEntries(typesRes.data.map(t => [t.id, t.kind_count || 0])));
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (item = null) => {
    if (item) {
      setEditing(item);
      setFormData({
        name: item.name,
        description: item.description || ''
      });
    } else {
      setEditing(null);
      setFormData({
        name: '',
        description: ''
      });
    }
    setDialogOpen(true);
  };

  const saveItem = async () => {
    if (!formData.name.trim()) {
      toast.error('İş tipi adı zorunludur');
      return;
    }

    try {
      if (editing) {
        await axios.put(`${API}/job-types/${editing.id}`, formData);
        toast.success('İş tipi güncellendi');
      } else {
        await axios.post(`${API}/job-types`, formData);
        toast.success('İş tipi eklendi');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const deleteItem = async (item) => {
    if (!(await confirmDialog({ title: 'İş tipi silinsin mi?', description: `"${item.name}" iş tipini ve bağlı tüm iş çeşitlerini silmek istediğinize emin misiniz?`, confirmLabel: 'Evet, Sil' }))) {
      return;
    }

    try {
      await axios.delete(`${API}/job-types/${item.id}`);
      toast.success('İş tipi silindi');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme işlemi başarısız');
    }
  };

  const activateItem = async (item) => {
    try {
      await axios.put(`${API}/job-types/${item.id}/activate`);
      toast.success('İş tipi tekrar aktif edildi. Bağlı iş çeşitlerini İş Çeşitleri sayfasından ayrıca aktif edebilirsiniz.');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Aktif etme başarısız');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
      </div>
    );
  }

  const activeTypes = jobTypes.filter(t => t.is_active);
  const passiveCount = jobTypes.length - activeTypes.length;
  const listTypes = showPassive ? jobTypes : activeTypes;

  return (
    <div data-testid="job-types-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">İş Tipleri</h1>
          <p className="text-slate-500 mt-1">Çalışma alanları için iş tipi tanımları</p>
        </div>
        {canCreate('work_areas') && (
          <Button 
            onClick={() => openDialog()} 
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="add-job-type-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            İş Tipi Ekle
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Briefcase className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Toplam İş Tipi</p>
                <p className="text-2xl font-bold text-slate-900">{activeTypes.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <ListTree className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Toplam İş Çeşidi</p>
                <p className="text-2xl font-bold text-slate-900">
                  {Object.values(jobKindsCounts).reduce((a, b) => a + b, 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <Link to="/job-kinds" className="block hover:opacity-80 transition-opacity">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <ChevronRight className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">İş Çeşitleri</p>
                    <p className="text-lg font-semibold text-slate-900">Yönetim Sayfası →</p>
                  </div>
                </div>
              </div>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle>İş Tipleri Listesi</CardTitle>
              <CardDescription>
                Tanımlanmış iş tiplerini görüntüleyin ve yönetin
              </CardDescription>
            </div>
            {canEdit('work_areas') && passiveCount > 0 && (
              <Button variant="outline" size="sm" onClick={() => setShowPassive(v => !v)} data-testid="toggle-passive-job-types">
                {showPassive ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                {showPassive ? 'Pasifleri Gizle' : `Pasifleri Göster (${passiveCount})`}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {listTypes.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Briefcase className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>Henüz iş tipi tanımlanmamış</p>
              <p className="text-sm mt-1">Yeni bir iş tipi ekleyerek başlayın</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>İş Tipi Adı</TableHead>
                  <TableHead>Açıklama</TableHead>
                  <TableHead className="text-center">İş Çeşidi Sayısı</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listTypes.map((item) => (
                  <TableRow key={item.id} data-testid={`job-type-row-${item.id}`} className={item.is_active ? '' : 'bg-slate-50 text-slate-400'}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4 text-slate-400" />
                        {item.name}
                        {!item.is_active && <Badge variant="outline" className="text-slate-500" data-testid={`job-type-passive-badge-${item.id}`}>Pasif</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-500 max-w-xs truncate">
                      {item.description || '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">
                        {jobKindsCounts[item.id] || 0} çeşit
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {!item.is_active ? (
                        <div className="flex justify-end">
                          <Button variant="outline" size="sm" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => activateItem(item)} data-testid={`activate-job-type-${item.id}`}>
                            <RotateCcw className="h-4 w-4 mr-1" />Aktif Et
                          </Button>
                        </div>
                      ) : (
                      <div className="flex justify-end gap-1">
                        <Link to={`/job-kinds?type=${item.id}`}>
                          <Button variant="ghost" size="sm" title="İş Çeşitlerini Görüntüle">
                            <ListTree className="h-4 w-4 text-blue-600" />
                          </Button>
                        </Link>
                        {canEdit('work_areas') && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openDialog(item)}
                            data-testid={`edit-job-type-${item.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canDelete('work_areas') && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-rose-600 hover:text-rose-700"
                            onClick={() => deleteItem(item)}
                            data-testid={`delete-job-type-${item.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? 'İş Tipi Düzenle' : 'Yeni İş Tipi Ekle'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">İş Tipi Adı *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Örn: Ortak Alan, Mağaza, Teknik Alan"
                data-testid="job-type-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Açıklama</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="İş tipi hakkında açıklama (opsiyonel)"
                rows={3}
                data-testid="job-type-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              İptal
            </Button>
            <Button onClick={saveItem} data-testid="save-job-type-btn">
              {editing ? 'Güncelle' : 'Ekle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobTypesPage;
